import { Container, Text, VStack, Tabs, TabList, Tab, IconButton, Flex, Hide } from '@telescope/cassini-ui';
import { useWidget, useBreakpointValue } from '@telescope/cassini-hooks';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import Slider from 'react-slick';
import { useBracketGrid } from '../providers/BracketGridProvider';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { tabsStyle } from './RoundNav.styles';
import { breakpoints } from 'utils/styles';
import { Open, Winner } from 'generated-types/widget';

const SLIDES_TO_SHOW = {
  MOBILE: 2,
  TABLET: 4,
};

export const RoundNav = () => {
  const {
    data: { content, styles },
  } = useWidget<Open | Winner>({ select: (data) => data.snapshot.snapshot_views.roundNavigation });

  const { displayedRound, setDisplayedRound, totalRounds } = useBracketGrid();
  const swipeable = useRef<Slider | null>(null);

  // Create tabs for each round. Since the number of rounds varies depending on the bracket size, we need to get the copy only for the rounds that are needed
  // (i.e. Round 4, and Round 5 are omitted for a 8 contestant bracket)
  const tabs = useMemo(
    () => [
      ...Array.from({ length: totalRounds }, (_, i) => {
        return content.buttons[`round${i + 1}`];
      }),
      { ...content.buttons.winner },
    ],
    [content.buttons, totalRounds]
  );

  useEffect(() => {
    swipeable?.current?.slickGoTo(displayedRound - 1);
  }, [displayedRound]);

  const getInitialSlide = useCallback(
    (slidesToShow: number) => {
      const maxInitialSlide = tabs.length - slidesToShow;
      const activeSlide = displayedRound - 1;
      return Math.min(activeSlide, maxInitialSlide);
    },
    [displayedRound, tabs.length]
  );

  const initialSlide = useBreakpointValue({
    base: getInitialSlide(SLIDES_TO_SHOW.MOBILE),
    md: getInitialSlide(SLIDES_TO_SHOW.TABLET),
  });

  const swipeSettings = {
    dots: false,
    infinite: false,
    arrows: false,
    draggable: false,
    slidesToShow: SLIDES_TO_SHOW.TABLET,
    slidesToScroll: 0,
    initialSlide,
    responsive: [
      {
        breakpoint: breakpoints.tablet,
        settings: {
          slidesToShow: SLIDES_TO_SHOW.MOBILE,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <Hide above="lg" ssr={false}>
      <Container className="RoundNav" {...styles?.root}>
        <Flex className="RoundNav__container" align={'center'} {...styles?.container}>
          <IconButton
            className="RoundNav__prev"
            isDisabled={displayedRound === 1}
            aria-label="Previous Round"
            icon={<FaChevronLeft />}
            variant="ghost"
            colorScheme="black"
            onClick={() => setDisplayedRound((current) => (current === 1 ? current : current - 1))}
          />
          <Tabs
            index={displayedRound - 1}
            onChange={(index) => setDisplayedRound(index + 1)}
            variant="line"
            isFitted
            flex={1}
            overflow={'hidden'}
            colorScheme="black"
            sx={tabsStyle}>
            <TabList>
              <Slider {...swipeSettings} ref={swipeable}>
                {tabs.map((tab, index) => (
                  <Tab key={index}>
                    <VStack>
                      <Text fontSize={['sm', 'lg']} fontWeight={600}>
                        {tab.label}
                      </Text>
                      <Text fontSize="xs">{tab.description}</Text>
                    </VStack>
                  </Tab>
                ))}
              </Slider>
            </TabList>
          </Tabs>

          <IconButton
            className="RoundNav__next"
            isDisabled={displayedRound === tabs.length}
            variant="ghost"
            aria-label="Next Round"
            icon={<FaChevronRight />}
            colorScheme="black"
            onClick={() => setDisplayedRound((current) => (current === tabs.length ? current : current + 1))}
          />
        </Flex>
      </Container>
    </Hide>
  );
};
